// ---------------------------------------------------------------------------
// Variable / Datasource Usage Index
//
// Pure functions + thin React hooks for tracking which flows and blocks
// reference each variable or datasource. Used by data explorer and
// relationship visualization layers.
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import type { Flow, BlockDef, UiElement } from '@cogo/types-v2'
import {
  analyzeFlowStateUsage,
  extractExpressionRefs,
  walkFlowSteps,
} from '@/features/flow-editor/services/flow-analysis'
import { useEditorStore } from '@/features/page-editor/store/editor-store'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VariableUsageEntry {
  flowName?: string
  blockName?: string
  mode: 'read' | 'write' | 'binding'
  stepAction?: string
}

export interface DatasourceUsageEntry {
  flowName?: string
  blockName?: string
  usage: 'fetch' | 'binding'
  stepAction?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Walk all elements in a UiElement tree. */
function walkElements(root: UiElement, fn: (el: UiElement) => void): void {
  fn(root)
  if (root.children) {
    for (const child of root.children) {
      walkElements(child, fn)
    }
  }
}

const DATA_ACTIONS = new Set([
  'restful_data_action',
  'graphql_action',
  'supabase_select',
  'supabase_insert',
  'supabase_update',
  'supabase_delete',
  'supabase_rpc',
])

// ---------------------------------------------------------------------------
// buildVariableUsageIndex
// ---------------------------------------------------------------------------

/**
 * Build a map of variable name -> usage entries from flows and block element trees.
 *
 * - Flows: uses analyzeFlowStateUsage for read/write detection
 * - Blocks: scans element text/props for #{var} expression bindings
 */
export function buildVariableUsageIndex(
  flows: Record<string, Flow>,
  blocks: Record<string, BlockDef>,
): Map<string, VariableUsageEntry[]> {
  const index = new Map<string, VariableUsageEntry[]>()

  function addEntry(key: string, entry: VariableUsageEntry) {
    if (!index.has(key)) index.set(key, [])
    index.get(key)!.push(entry)
  }

  // --- From flows ---
  for (const [flowName, flow] of Object.entries(flows)) {
    if (!flow?.steps) continue
    const stateEntries = analyzeFlowStateUsage(flow)
    for (const entry of stateEntries) {
      addEntry(entry.key, {
        flowName,
        mode: entry.mode,
        stepAction: entry.stepAction,
      })
    }
  }

  // --- From block element trees (binding refs) ---
  for (const [blockName, block] of Object.entries(blocks)) {
    const root = block?.ui?.root
    if (!root) continue
    walkElements(root, (el) => {
      // Check text content
      const textRefs = extractExpressionRefs(el.text ?? '')
      for (const ref of textRefs) {
        addEntry(ref, { blockName, mode: 'binding' })
      }

      // Check src, alt, and other string props that may contain expressions
      for (const prop of ['src', 'alt', 'name', 'placeholder'] as const) {
        const val = (el as unknown as Record<string, unknown>)[prop] // TS2352 bridge — UiElement has no index signature; shallow prop read by known key list
        if (typeof val === 'string') {
          const refs = extractExpressionRefs(val)
          for (const ref of refs) {
            addEntry(ref, { blockName, mode: 'binding' })
          }
        }
      }
    })
  }

  return index
}

// ---------------------------------------------------------------------------
// buildDatasourceUsageIndex
// ---------------------------------------------------------------------------

/**
 * Build a map of datasource name -> usage entries from flows and block elements.
 *
 * - Flows: detects data fetch actions with params.source or params.datasource
 * - Blocks: detects iterateOver and dataSource references in elements
 */
export function buildDatasourceUsageIndex(
  flows: Record<string, Flow>,
  blocks: Record<string, BlockDef>,
): Map<string, DatasourceUsageEntry[]> {
  const index = new Map<string, DatasourceUsageEntry[]>()

  function addEntry(key: string, entry: DatasourceUsageEntry) {
    if (!index.has(key)) index.set(key, [])
    index.get(key)!.push(entry)
  }

  // --- From flows ---
  for (const [flowName, flow] of Object.entries(flows)) {
    if (!flow?.steps) continue
    walkFlowSteps(flow.steps, (step) => {
      if ((step.type === 'basic' || step.type === 'expression') && DATA_ACTIONS.has(step.action)) {
        const source = step.params?.datasource ?? step.params?.source
        if (typeof source === 'string' && source) {
          addEntry(source, { flowName, usage: 'fetch', stepAction: step.action })
        }
      }
    })
  }

  // --- From block element trees ---
  for (const [blockName, block] of Object.entries(blocks)) {
    const root = block?.ui?.root
    if (!root) continue
    walkElements(root, (el) => {
      // iterateOver references a datasource/variable
      if (el.iterateOver && typeof el.iterateOver === 'string') {
        // Extract root name (before any dot path)
        const dsName = el.iterateOver.split('.')[0]
        if (dsName) {
          addEntry(dsName, { blockName, usage: 'binding' })
        }
      }
      // dataSource prop
      const ds = (el as unknown as Record<string, unknown>).dataSource // TS2352 bridge — UiElement has no index signature; shallow dataSource read
      if (typeof ds === 'string' && ds) {
        addEntry(ds, { blockName, usage: 'binding' })
      }
    })
  }

  return index
}

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

export function getVariableUsages(
  index: Map<string, VariableUsageEntry[]>,
  varName: string,
): VariableUsageEntry[] {
  return index.get(varName) ?? []
}

export function getDatasourceUsages(
  index: Map<string, DatasourceUsageEntry[]>,
  dsName: string,
): DatasourceUsageEntry[] {
  return index.get(dsName) ?? []
}

// ---------------------------------------------------------------------------
// React hooks
// ---------------------------------------------------------------------------

/** Memoized variable usage index from EditorStore blocks + flows. */
export function useVariableUsageIndex(): Map<string, VariableUsageEntry[]> {
  const blocks = useEditorStore((s) => s.blocks)
  const flows = useEditorStore((s) => s.flows)
  return useMemo(
    () => buildVariableUsageIndex(flows as Record<string, Flow>, blocks as Record<string, BlockDef>),
    [flows, blocks],
  )
}

/** Memoized datasource usage index from EditorStore blocks + flows. */
export function useDatasourceUsageIndex(): Map<string, DatasourceUsageEntry[]> {
  const blocks = useEditorStore((s) => s.blocks)
  const flows = useEditorStore((s) => s.flows)
  return useMemo(
    () => buildDatasourceUsageIndex(flows as Record<string, Flow>, blocks as Record<string, BlockDef>),
    [flows, blocks],
  )
}
